'use client'

import { useDialogActions } from '@/components/dialog'
import { DEFAULT_LANG_CODE, getI18n, I18nDictionary, LangCode } from '@/components/i18n/lang-context'
import MD from '@/components/markdown'
import Reveal from '@/components/reveal'
import { Card } from '@/components/ui/card'
import { Flex, FlexCol } from '@/components/ui/flex'
import { buildEmailUrl, buildPhoneUrl, crop, slugify } from '@/lib/utils/utils'
import { generateVCardString } from '@/lib/vcard/vcard-utils'
import Image from 'next/image'
import React, { MouseEvent } from 'react'
import { FaAddressCard, FaCopy, FaEnvelopeSquare, FaFacebookSquare, FaInstagramSquare, FaLinkedin, FaPhoneSquare, FaShareAlt, FaTwitterSquare, FaYoutubeSquare } from 'react-icons/fa'
import { Author } from '../author'
import { useCookiesConsentDialog } from '../cookies-dialog'


const I18N: I18nDictionary = {
  copy: { en: 'Copy', fr: 'Copier' },
  share: { en: 'Share', fr: 'Partager' },
  linkTo: { en: 'link to', fr: 'lien vers' },
  of: { en: 'of', fr: 'de' },
  the: { en: '', fr: 'le ' },
  profile: { en: 'profile', fr: 'profil' },
  email: { en: 'email', fr: 'mail' },
  phone: { en: 'phone number', fr: 'numéro de téléphone' },
  downloadVCard: { en: 'Download vCard', fr: 'Télécharger la vCard' }
}

export function useAuthorDialog() {
  const { openDialog } = useDialogActions()
  let langCode: LangCode = DEFAULT_LANG_CODE
  const { openCookiesConsentDialog } = useCookiesConsentDialog(langCode)

  const _openCookiesConsentDialog = async (langCode: LangCode) => {
    openCookiesConsentDialog('welcome', langCode, true)
  }
  const openAuthorDialog = (author: Author, langCode: LangCode) => {
    openDialog({ content: <AuthorDialogContent author={author} langCode={langCode} />, onCloseAfter: () => _openCookiesConsentDialog(langCode) })
  }

  return { openAuthorDialog }
}

export function AuthorDialogContent({ author, langCode }: { author: Author, langCode: LangCode }) {

  const i18n = getI18n(I18N, langCode)

  const copy = (event: MouseEvent, content: string) => {
    event.stopPropagation()
    event.preventDefault()
    navigator.clipboard.writeText(content)
  }
  const share = (event: MouseEvent, data: string, title: string) => {
    event.stopPropagation()
    event.preventDefault()
    title = title.substring(0, 1).toUpperCase() + title.substring(1)
    navigator.share({
      title: data.startsWith('http') ? title : undefined,
      url: data.startsWith('http') ? data : undefined,
      text: !data.startsWith('http') ? title + ' → ' + data : undefined
    })
  }
  const getEmailUrl = (currentAuthor: Author) => currentAuthor.email && buildEmailUrl(currentAuthor.email)
  const getPhoneUrl = (currentAuthor: Author) => currentAuthor.phone && buildPhoneUrl(currentAuthor.phone)
  const getLinkedInUserUrl = (currentAuthor: Author) => currentAuthor.linkedInUserId && `https://www.linkedin.com/in/${currentAuthor.linkedInUserId}/`
  const getTwitterUrl = (currentAuthor: Author) => currentAuthor.twitterId && `https://x.com/${currentAuthor.twitterId}/`
  const getInstagramUrl = (currentAuthor: Author) => currentAuthor.instagramId && `https://www.instagram.com/${currentAuthor.instagramId}/`
  const getFacebookUrl = (currentAuthor: Author) => currentAuthor.facebookId && `https://www.facebook.com/${currentAuthor.facebookId}/`
  const getYoutubeUrl = (currentAuthor: Author) => currentAuthor.youtubeId && `https://www.youtube.com/@${currentAuthor.youtubeId}/`
  const getLinkedInShowcaseUrl = (currentAuthor: Author) => currentAuthor.linkedInShowcaseId && `https://www.linkedin.com/showcase/${currentAuthor.linkedInShowcaseId}/`
  const getLinkedInCompanyUrl = (currentAuthor: Author) => currentAuthor.linkedInCompanyId && `https://www.linkedin.com/company/${currentAuthor.linkedInCompanyId}/`

  const getProfileEntry = (link: string | null | undefined, value: string, text: string, name: string, type: string, icon: React.ReactNode) => {
    const title = type + ' ' + i18n.of + ' ' + name
    return link &&
      <Flex className='mt-4 flex-row items-center justify-center'>
        <div className='ml-5 mr-7 w-4 opacity-70 sm:ml-24'>
          {icon}
        </div>
        <MD textAlign='center' className='grow font-medium sm:text-lg' target='_blank' langCode={langCode}>
          {'[' + (!text ? type : text) + '](' + link + ')'}
        </MD>
        <div id='ENTRY_COPY' className={'h-5 w-5 cursor-pointer opacity-50 hover:opacity-100'}
          title={i18n.copy + ' ' + title}
          onClick={(event) => copy(event, value)}>
          <FaCopy className='h-5 w-5'/>
        </div>
        <div id='ENTRY_SHARE' className={'ml-2 mr-5 h-5 w-5 cursor-pointer opacity-50 hover:opacity-100 sm:mr-24'}
          title={i18n.share + ' ' + title}
          onClick={(event) => share(event, value, title)}>
          <FaShareAlt className='h-5 w-5'/>
        </div>
      </Flex>
  }

  return (
    <Card variant='consent' className='w-full p-1 sm:w-fit sm:min-w-[640px] sm:max-w-[640px] sm:p-4' style={{ margin: '20px' }}>
      <div id='PROFILE_ACTIONS' className={'text-main-dark sm:text-main-lightest absolute right-2 top-4 m-4 flex flex-col sm:flex-row'}>
        <button
          title={i18n.downloadVCard + ' ' + i18n.of + ' ' + author.name}
          className={'ml-4 mt-4 h-5 w-5 cursor-pointer border-none bg-transparent p-0 opacity-50 hover:opacity-100'}
          onClick={async (e) => {
            e.stopPropagation()
            try {
              const vCardString = await generateVCardString(author, langCode)
              const dataUri = `data:text/vcard;charset=utf-8,${encodeURIComponent(vCardString)}`
              const link = document.createElement('a')
              link.href = dataUri
              link.download = `${slugify(author.name)}.vcf`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            } catch (error) {
              console.error('Error generating or downloading vCard:', error)
              alert('Error generating vCard.')
            }
          }}
        >
          <FaAddressCard className='h-full w-full' />
        </button>
      </div>

      <Flex className='justify-center gap-8 lg:gap-16'>
        <FlexCol className='flex-col items-center'>
          <MD className='font-medium sm:text-lg' langCode={langCode}>{'***' + author.name + '***'}</MD>
          <MD className='font-medium opacity-70' langCode={langCode}>{'**' + author.title[langCode] + '**'}</MD>
        </FlexCol>
      </Flex>
      <Flex className='justify-center p-5'>
        <Reveal id='HEAD_SEPARATOR' delay={100} translateX={100} duration={350} key={author.name}
          className='relative flex aspect-square w-1/3 flex-col items-center gap-4 overflow-hidden rounded-full shadow-sm  transition-all hover:shadow-md sm:w-1/4 lg:w-1/3 lg:gap-8'>
          <Image src={author.photo.url} alt={author.name} className='transition-all hover:scale-105'
            width={0}
            height={0}
            sizes='250px'
            style={{ objectFit: 'cover' }}
            fill />
        </Reveal>
      </Flex>

      {getProfileEntry(getEmailUrl(author), author.email, author.email, author.name, i18n.email, <FaEnvelopeSquare className='h-6 w-6' />)}
      {getProfileEntry(getPhoneUrl(author), author.phone, author.phone, author.name, i18n.phone, <FaPhoneSquare className='h-6 w-6' />)}
      {getProfileEntry(getLinkedInUserUrl(author), author.linkedInUserId, author.teamOrder < 0 ? '' : crop(author.linkedInUserId, 20), author.name, 'LinkedIn', <FaLinkedin className='h-6 w-6' />)}
      {getProfileEntry(getLinkedInShowcaseUrl(author), author.linkedInShowcaseId, author.teamOrder < 0 ? '' : crop(author.linkedInShowcaseId, 20), author.name, 'LinkedIn', <FaLinkedin className='h-6 w-6' />)}
      {getProfileEntry(getLinkedInCompanyUrl(author), author.linkedInCompanyId, author.teamOrder < 0 ? '' : crop(author.linkedInCompanyId, 20), author.name, 'LinkedIn', <FaLinkedin className='h-6 w-6' />)}
      {getProfileEntry(getTwitterUrl(author), author.twitterId, author.teamOrder < 0 ? '' : crop('@' + author.twitterId, 20), author.name, 'Twitter/X', <FaTwitterSquare className='h-6 w-6' />)}
      {getProfileEntry(getInstagramUrl(author), author.instagramId, author.teamOrder < 0 ? '' : crop(author.instagramId, 20), author.name, 'Instagram', <FaInstagramSquare className='h-6 w-6' />)}
      {getProfileEntry(getFacebookUrl(author), author.facebookId, author.teamOrder < 0 ? '' : crop(author.facebookId, 20), author.name, 'Facebook', <FaFacebookSquare className='h-6 w-6' />)}
      {getProfileEntry(getYoutubeUrl(author), author.youtubeId, author.teamOrder < 0 ? '' : crop('@' + author.youtubeId, 20), author.name, 'Youtube', <FaYoutubeSquare className='h-6 w-6' />)}
    </Card>
  )
}
